import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Save, Send, Undo2, Link as LinkIcon, Download, ImagePlus, GripVertical, Sparkles,
} from "lucide-react";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtPeriod } from "../lib/format";
import { AttachmentEditor } from "../components/report/AttachmentEditor";
import { ReportViews } from "../components/report/ReportViews";
import { DEFAULT_STAGES } from "../components/report/ExpansionView";
import { StageControls } from "../components/report/ExpansionStages";
import ReportView from "./ReportView";
import { exportNodeToPdfPaged } from "../lib/pdf";

const KPI_KEYS = [
  ["total_revenue", "Total Revenue", "currency"],
  ["net_income", "Net Income", "currency"],
  ["total_expenses", "Total Expenses", "currency"],
  ["occupancy_pct", "Occupancy", "percent"],
];

const inputCls = "w-full bg-brand-page border border-brand-border rounded-md px-3 py-2.5 text-sm text-brand-ink focus:outline-none focus:border-brand-primary transition-colors";
const labelCls = "block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5";

function Field({ label, children }) {
  return <div><label className={labelCls}>{label}</label>{children}</div>;
}

function monthRange(a, b) {
  if (!a || !b) return [];
  const out = [];
  let [y, m] = [parseInt(a.slice(0, 4), 10), parseInt(a.slice(5, 7), 10)];
  const [by, bm] = [parseInt(b.slice(0, 4), 10), parseInt(b.slice(5, 7), 10)];
  while (y < by || (y === by && m <= bm)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

export default function ReportEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const previewRef = useRef(null);

  const [meta, setMeta] = useState(null);
  const [report, setReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // form state
  const [title, setTitle] = useState("");
  const [intro, setIntro] = useState("");
  const [location, setLocation] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [projects, setProjects] = useState([]);
  const [goals, setGoals] = useState([]);
  const [sections, setSections] = useState([]);
  const [images, setImages] = useState([]);
  const [includeExpansion, setIncludeExpansion] = useState(false);
  const [expansion, setExpansion] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [status, setStatus] = useState("draft");
  const [shareToken, setShareToken] = useState(null);
  const [includeBreakdown, setIncludeBreakdown] = useState(false);
  const [includeSales, setIncludeSales] = useState(false);
  const [includeBudget, setIncludeBudget] = useState(false);
  const [budgetGap, setBudgetGap] = useState(null);
  const [includeMarketing, setIncludeMarketing] = useState(false);
  const [locBreakdown, setLocBreakdown] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);
  const [marketingSummary, setMarketingSummary] = useState(null);
  const [kpiMonthly, setKpiMonthly] = useState([]);
  const [salesMonthly, setSalesMonthly] = useState([]);
  const [locBreakdownMonthly, setLocBreakdownMonthly] = useState({});

  const loadAll = useCallback(async () => {
    const [{ data: m }, { data: r }] = await Promise.all([
      api.get("/financials/meta"),
      api.get(`/reports/${id}`),
    ]);
    setMeta(m);
    setReport(r);
    setTitle(r.title || ""); setIntro(r.intro || ""); setLocation(r.location || "all");
    setStart(r.start || ""); setEnd(r.end || "");
    setProjects(r.projects?.length ? r.projects : []);
    setGoals(r.goals?.length ? r.goals : []);
    setSections(r.sections?.length ? r.sections : []);
    setImages(r.images || []);
    setIncludeExpansion(!!r.include_expansion);
    setExpansion(r.expansion || []);
    setKpis(r.kpis || []);
    setStatus(r.status || "draft");
    setShareToken(r.share_token);
    setIncludeBreakdown(!!r.include_location_breakdown);
    setIncludeSales(!!r.include_sales);
    setIncludeBudget(!!r.include_budget);
    setIncludeMarketing(!!r.include_marketing);
    setLocBreakdown(r.location_breakdown || []);
    setSalesSummary(r.sales_summary || null);
    setMarketingSummary(r.marketing_summary || null);
  }, [id]);

  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user, loadAll]);

  useEffect(() => {
    if (!includeBudget || !start || !end) { setBudgetGap(null); return; }
    api.get("/budget/kpis", { params: { location, start, end, by_location: location === "all" } }).then(({ data }) => setBudgetGap(data)).catch(() => setBudgetGap(null));
  }, [includeBudget, location, start, end]);

  // live KPI preview whenever period/location changes
  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    api.get("/financials/summary", { params: { location, start, end, compare: "prior" } })
      .then(({ data }) => {
        if (cancelled) return;
        const totals = data.totals || {};
        const deltas = data.comparison?.deltas || {};
        setKpis(KPI_KEYS.map(([key, label, format]) => ({
          key, label, format,
          value: totals[key] ?? null,
          delta_abs: deltas[key]?.abs ?? null,
          delta_pct: deltas[key]?.pct ?? null,
        })));
      })
      .catch(() => { if (!cancelled) setKpis(KPI_KEYS.map(([key, label, format]) => ({ key, label, format, value: null }))); });
    // monthly KPI series for the drill-down preview
    api.get("/financials/trend", { params: { location } })
      .then(({ data }) => {
        if (cancelled) return;
        const rows = (data.trend || []).filter((t) => t.period >= start && t.period <= end);
        setKpiMonthly(rows.length > 1 ? rows.map((t) => ({
          period: t.period, total_revenue: t.total_revenue, net_income: t.net_income,
          total_expenses: t.total_expenses, occupancy_pct: t.occupancy_pct,
        })) : []);
      })
      .catch(() => { if (!cancelled) setKpiMonthly([]); });
    return () => { cancelled = true; };
  }, [location, start, end]);

  // live fetch of toggled add-ons for the preview
  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    if (includeBreakdown && location === "all") {
      api.get("/financials/locations-breakdown", { params: { start, end } })
        .then(({ data }) => { if (!cancelled) setLocBreakdown((data.locations || []).map((l) => ({ location: l.location, total_revenue: l.total_revenue, net_income: l.net_income, occupancy_pct: l.occupancy_pct }))); })
        .catch(() => { if (!cancelled) setLocBreakdown([]); });
      // per-location monthly series for the drill-down preview
      const months = monthRange(start, end);
      if (months.length > 1) {
        Promise.all(months.map((p) => api.get("/financials/locations-breakdown", { params: { start: p, end: p } })
          .then(({ data }) => ({ p, rows: data.locations || [] })).catch(() => ({ p, rows: [] }))))
          .then((results) => {
            if (cancelled) return;
            const by = {};
            results.forEach(({ p, rows }) => rows.forEach((l) => {
              (by[l.location] = by[l.location] || []).push({ period: p, total_revenue: l.total_revenue, net_income: l.net_income, occupancy_pct: l.occupancy_pct });
            }));
            Object.values(by).forEach((arr) => arr.sort((a, b) => a.period.localeCompare(b.period)));
            setLocBreakdownMonthly(by);
          });
      } else {
        setLocBreakdownMonthly({});
      }
    } else {
      setLocBreakdown([]);
      setLocBreakdownMonthly({});
    }
    if (includeSales) {
      api.get("/sales", { params: { location: location === "all" ? "All Locations" : location, start, end } })
        .then(({ data }) => {
          if (cancelled) return;
          setSalesSummary(data.summary || null);
          const ms = data.months || [];
          setSalesMonthly(ms.length > 1 ? ms.map((m) => ({
            period: m.period, tours_booked: m.tours_booked, tours_completed: m.tours_completed,
            won_deals: m.won_deals, actual_revenue: m.actual_revenue,
          })) : []);
        })
        .catch(() => { if (!cancelled) { setSalesSummary(null); setSalesMonthly([]); } });
    } else {
      setSalesSummary(null);
      setSalesMonthly([]);
    }
    if (includeMarketing) {
      api.get("/marketing")
        .then(({ data }) => {
          if (cancelled) return;
          const rows = (data.months || []).filter((m) => m.period >= start && m.period <= end);
          if (!rows.length) { setMarketingSummary(null); return; }
          const spend = rows.reduce((a, m) => a + (m.spend || 0), 0);
          const conv = rows.reduce((a, m) => a + (m.conversions || 0), 0);
          const clicks = rows.reduce((a, m) => a + (m.clicks || 0), 0);
          const impr = rows.reduce((a, m) => a + (m.impressions || 0), 0);
          setMarketingSummary({
            spend: Math.round(spend * 100) / 100, conversions: conv, clicks, impressions: impr,
            cpa: conv ? Math.round(spend / conv * 100) / 100 : null,
            ctr: impr ? Math.round(clicks / impr * 10000) / 100 : null,
            conv_rate: clicks ? Math.round(conv / clicks * 10000) / 100 : null,
            avg_cpc: clicks ? Math.round(spend / clicks * 100) / 100 : null, months: rows.length,
          });
        })
        .catch(() => { if (!cancelled) setMarketingSummary(null); });
    } else {
      setMarketingSummary(null);
    }
    return () => { cancelled = true; };
  }, [location, start, end, includeBreakdown, includeSales, includeMarketing]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  if (!meta || !report) {
    return <div className="min-h-screen bg-brand-page relative z-10"><Header /><div className="flex items-center justify-center h-[70vh]"><div className="h-10 w-10 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div></div>;
  }

  const periodLabel = (() => {
    if (!start) return "";
    if (start === end) return fmtPeriod(start);
    const sy = start.slice(0, 4), ey = end.slice(0, 4), sm = parseInt(start.slice(5), 10), em = parseInt(end.slice(5), 10);
    if (sy === ey && [1, 4, 7, 10].includes(sm) && em === sm + 2) return `Q${{ 1: 1, 4: 2, 7: 3, 10: 4 }[sm]} ${sy}`;
    return `${fmtPeriod(start)} – ${fmtPeriod(end)}`;
  })();

  const locationLabel = meta.locations.find((l) => l.name === location)?.label || location;
  const draft = {
    title: title || "Untitled Report", intro, location, location_label: locationLabel, period_label: periodLabel,
    kpis, projects, goals, sections, images, include_expansion: includeExpansion, expansion,
    include_location_breakdown: includeBreakdown, location_breakdown: locBreakdown,
    include_sales: includeSales, sales_summary: salesSummary,
    include_marketing: includeMarketing, marketing_summary: marketingSummary,
    include_budget: includeBudget, budget_gap: budgetGap,
    kpi_monthly: kpiMonthly, sales_monthly: salesMonthly, location_breakdown_monthly: locBreakdownMonthly,
  };

  const body = () => ({
    title: title.trim() || "Untitled Report", intro, location, start, end, compare: "prior",
    include_location_breakdown: includeBreakdown, include_sales: includeSales, include_marketing: includeMarketing, include_budget: includeBudget,
    projects, goals, sections, images, include_expansion: includeExpansion, expansion,
  });

  const save = async (silent = false) => {
    if (!start || !end) { toast.error("Choose a reporting period."); return null; }
    setSaving(true);
    try {
      const { data } = await api.put(`/reports/${id}`, body());
      setKpis(data.kpis || []); setStatus(data.status || "draft"); setShareToken(data.share_token);
      if (!silent) toast.success("Report saved");
      return data;
    } catch (err) {
      toast.error("Save failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
      return null;
    } finally { setSaving(false); }
  };

  const togglePublish = async () => {
    const saved = await save(true);
    if (!saved) return;
    const publish = status !== "published";
    try {
      const { data } = await api.post(`/reports/${id}/publish`, { published: publish });
      setStatus(data.status);
      toast.success(publish ? "Report published" : "Moved back to draft",
        { description: publish ? "Anyone with the link can now view it." : "The share link is no longer live." });
    } catch (err) { toast.error("Action failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/r/${shareToken}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied", { description: url }); }
    catch { toast.error("Couldn't copy the link"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try { await exportNodeToPdfPaged(previewRef.current, `${(title || "report").replace(/[^a-z0-9]+/gi, "-")}.pdf`); }
    catch { toast.error("Could not generate the PDF."); }
    finally { setDownloading(false); }
  };

  const generateSummary = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/reports/generate-summary", {
        title, location_label: locationLabel, period_label: periodLabel, kpis, projects, goals, sections,
      });
      setIntro(data.summary);
      toast.success("Summary generated", { description: "Edit it however you like." });
    } catch (err) {
      toast.error("Generation failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setGenerating(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/reports/${id}/images`, fd);
      setImages((p) => [...p, data]);
      toast.success("Image added");
    } catch (err) { toast.error("Upload failed", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  const setQuarter = (y, q) => {
    const sm = { 1: "01", 2: "04", 3: "07", 4: "10" }[q];
    const em = { 1: "03", 2: "06", 3: "09", 4: "12" }[q];
    setStart(`${y}-${sm}`); setEnd(`${y}-${em}`);
  };
  const years = [...new Set((meta.periods || []).map((p) => p.slice(0, 4)))].sort().reverse();
  const latestYear = years[0] || new Date().getFullYear().toString();

  const listAdd = (setter) => (empty) => setter((p) => [...p, empty]);
  const listSet = (setter) => (i, field, val) => setter((p) => p.map((it, idx) => idx === i ? { ...it, [field]: val } : it));
  const listDel = (setter) => (i) => setter((p) => p.filter((_, idx) => idx !== i));

  const moveAttachment = (from, to) => {
    const lists = { projects, goals, sections, expansion };
    const setters = { projects: setProjects, goals: setGoals, sections: setSections, expansion: setExpansion };
    const [fl, fi] = from.key.split(":"); const [tl, ti] = to.key.split(":");
    if (from.key === to.key && (to.index === from.index || to.index === from.index + 1)) return;
    const next = { ...lists, [fl]: lists[fl].map((it) => ({ ...it, attachments: [...(it.attachments || [])] })) };
    if (tl !== fl) next[tl] = lists[tl].map((it) => ({ ...it, attachments: [...(it.attachments || [])] }));
    const [item] = next[fl][+fi].attachments.splice(from.index, 1);
    const idx = from.key === to.key && from.index < to.index ? to.index - 1 : to.index;
    next[tl][+ti].attachments.splice(idx, 0, item);
    setters[fl](next[fl]);
    if (tl !== fl) setters[tl](next[tl]);
  };

  const barBtn = "inline-flex items-center gap-2 text-sm px-4 py-2 transition-all active:scale-[0.98]";

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      {/* action bar */}
      <div className="sticky top-16 z-20 bg-brand-page/90 backdrop-blur-xl border-b border-brand-border">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-14 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/reports")} data-testid="report-back" className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-ink"><ArrowLeft className="h-4 w-4" /> All reports</button>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-[0.12em] rounded-full px-2 py-0.5 ${status === "published" ? "text-brand-pos bg-brand-pos/10" : "text-brand-gold bg-brand-gold/10"}`}>{status === "published" ? "Published" : "Draft"}</span>
            <ReportViews rid={id} shareToken={shareToken} published={status === "published"} />
            {status === "published" && <button onClick={copyLink} data-testid="report-copylink" className={`${barBtn} text-brand-muted hover:text-brand-primary`}><LinkIcon className="h-4 w-4" /> Copy link</button>}
            <button onClick={downloadPdf} disabled={downloading} data-testid="report-download" className={`${barBtn} text-brand-muted hover:text-brand-primary disabled:opacity-60`}><Download className="h-4 w-4" /> {downloading ? "Preparing…" : "PDF"}</button>
            <button onClick={togglePublish} data-testid="report-publish" className={`${barBtn} border ${status === "published" ? "border-brand-border text-brand-muted hover:text-brand-ink" : "border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-brand-page"}`}>
              {status === "published" ? <><Undo2 className="h-4 w-4" /> Unpublish</> : <><Send className="h-4 w-4" /> Publish</>}
            </button>
            <button onClick={() => save(false)} disabled={saving} data-testid="report-save" className={`${barBtn} bg-brand-primary text-brand-page hover:bg-brand-primaryHover disabled:opacity-60`}><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* FORM */}
        <div className="space-y-8" data-testid="report-form">
          <section className="space-y-4">
            <Field label="Report Title"><input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="report-title" placeholder="Q3 2026 Investor Update" className={`${inputCls} font-serif text-lg`} /></Field>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={`${labelCls} mb-0`}>Intro / Executive Summary</label>
                <button onClick={generateSummary} disabled={generating} data-testid="report-generate-summary"
                  className="inline-flex items-center gap-1.5 text-[11px] text-brand-primary hover:text-brand-primaryHover disabled:opacity-60">
                  <Sparkles className="h-3.5 w-3.5" /> {generating ? "Generating…" : "Generate with AI"}
                </button>
              </div>
              <textarea value={intro} onChange={(e) => setIntro(e.target.value)} data-testid="report-intro" rows={4} placeholder="A short note to set the stage — or generate one with AI from your numbers." className={`${inputCls} resize-none`} />
            </div>
          </section>

          {/* Period + location */}
          <section className="space-y-4">
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary border-b border-brand-border pb-2">Numbers · auto-pulled</p>
            <Field label="Location">
              <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="report-location" className={inputCls}>
                {meta.locations.map((l) => <option key={l.name} value={l.name}>{l.label}</option>)}
              </select>
            </Field>
            <div>
              <label className={labelCls}>Quick Quarter ({latestYear})</label>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4].map((q) => (
                  <button key={q} onClick={() => setQuarter(latestYear, q)} data-testid={`report-quarter-${q}`}
                    className="px-3 py-1.5 text-xs border border-brand-border rounded-full text-brand-muted hover:border-brand-primary hover:text-brand-primary transition-colors">Q{q} {latestYear}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="From"><input type="month" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" className={inputCls} /></Field>
              <Field label="To"><input type="month" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" className={inputCls} /></Field>
            </div>
            <p className="text-[11px] text-brand-tertiary">KPIs (Revenue, Net Income, Expenses, Occupancy) auto-fill from these numbers with period-over-period change.</p>
            {meta.latest_period && end && end < meta.latest_period && (
              <div data-testid="report-newer-data" className="flex items-center justify-between gap-3 rounded-md border border-brand-gold/40 bg-brand-gold/10 px-3 py-2">
                <p className="text-[11px] text-brand-ink">
                  Newer financial data is available through <span className="font-semibold">{fmtPeriod(meta.latest_period)}</span>. This report still ends at {fmtPeriod(end)}.
                </p>
                <button
                  onClick={() => setEnd(meta.latest_period)}
                  data-testid="report-bump-latest"
                  className="shrink-0 px-2.5 py-1 text-[11px] rounded-full border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-brand-page transition-colors">
                  Update to {fmtPeriod(meta.latest_period)}
                </button>
              </div>
            )}
            <div className="space-y-2.5 pt-1">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={includeBudget} onChange={(e) => setIncludeBudget(e.target.checked)} data-testid="toggle-budget" className="h-4 w-4 accent-brand-primary cursor-pointer" />
                <span className="text-sm text-brand-ink">Show "vs budget" on KPI tiles <span className="text-brand-tertiary">{includeBudget && budgetGap && !budgetGap.months?.length ? "(no budget for this period yet)" : "(months with a budget)"}</span></span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={includeBreakdown} onChange={(e) => setIncludeBreakdown(e.target.checked)} data-testid="toggle-breakdown" className="h-4 w-4 accent-brand-primary cursor-pointer" />
                <span className="text-sm text-brand-ink">Include per-location breakdown <span className="text-brand-tertiary">(All Locations only)</span></span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={includeSales} onChange={(e) => setIncludeSales(e.target.checked)} data-testid="toggle-sales" className="h-4 w-4 accent-brand-primary cursor-pointer" />
                <span className="text-sm text-brand-ink">Include sales &amp; leasing summary</span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" checked={includeMarketing} onChange={(e) => setIncludeMarketing(e.target.checked)} data-testid="toggle-marketing" className="h-4 w-4 accent-brand-primary cursor-pointer" />
                <span className="text-sm text-brand-ink">Include marketing (PPC) summary</span>
              </label>
            </div>
          </section>

          {/* Expansion */}
          <section className="space-y-3" data-testid="report-expansion-editor">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input type="checkbox" checked={includeExpansion} onChange={(e) => setIncludeExpansion(e.target.checked)} data-testid="toggle-expansion" className="h-4 w-4 accent-brand-primary cursor-pointer" />
              <span className="text-sm text-brand-ink">Include Expansion section <span className="text-brand-tertiary">(new locations in progress)</span></span>
            </label>
            {includeExpansion && (
              <ListEditor title="Expansion Projects" testid="expansion" items={expansion}
                onAdd={() => { const st = expansion[expansion.length - 1]?.stages?.length ? expansion[expansion.length - 1].stages : DEFAULT_STAGES; listAdd(setExpansion)({ name: "", stage: st[0], stages: st, target_date: "", notes: "", attachments: [] }); }}
                onDel={listDel(setExpansion)}
                render={(x, i) => (
                  <>
                    <input value={x.name} onChange={(e) => listSet(setExpansion)(i, "name", e.target.value)} data-testid={`expansion-name-${i}`} placeholder="e.g. Hoboken — 77 River St" className={`${inputCls} mb-2 font-serif`} />
                    <StageControls x={x} i={i} inputCls={inputCls} onChange={(patch) => setExpansion((p) => p.map((it, k) => (k === i ? { ...it, ...patch } : it)))} />
                    <label className="flex items-center gap-2 mb-2"><span className="text-[11px] text-brand-tertiary shrink-0">Target open</span>
                      <input type="month" value={x.target_date} onChange={(e) => listSet(setExpansion)(i, "target_date", e.target.value)} data-testid={`expansion-date-${i}`} className={inputCls} />
                    </label>
                    <textarea value={x.notes} onChange={(e) => listSet(setExpansion)(i, "notes", e.target.value)} data-testid={`expansion-notes-${i}`} rows={3} placeholder="Progress notes — where things stand, next milestones" className={`${inputCls} resize-none`} />
                    <AttachmentEditor reportId={id} items={x.attachments || []} onChange={(v) => listSet(setExpansion)(i, "attachments", v)} testid={`expansion-${i}`} dndKey={`expansion:${i}`} onMove={moveAttachment} />
                  </>
                )} />
            )}
          </section>

          {/* Projects */}
          <ListEditor title="Projects & Initiatives" testid="projects" items={projects}
            onAdd={() => listAdd(setProjects)({ title: "", description: "" })}
            onDel={listDel(setProjects)}
            render={(p, i) => (
              <>
                <input value={p.title} onChange={(e) => listSet(setProjects)(i, "title", e.target.value)} data-testid={`project-title-${i}`} placeholder="Project name" className={`${inputCls} mb-2`} />
                <textarea value={p.description} onChange={(e) => listSet(setProjects)(i, "description", e.target.value)} data-testid={`project-desc-${i}`} rows={2} placeholder="What happened / impact" className={`${inputCls} resize-none`} />
                <AttachmentEditor reportId={id} items={p.attachments || []} onChange={(v) => listSet(setProjects)(i, "attachments", v)} testid={`project-${i}`} dndKey={`projects:${i}`} onMove={moveAttachment} />
              </>
            )} />

          {/* Goals */}
          <ListEditor title="Goals for Next Quarter" testid="goals" items={goals}
            onAdd={() => listAdd(setGoals)({ text: "" })}
            onDel={listDel(setGoals)}
            render={(g, i) => (
              <>
                <input value={g.text} onChange={(e) => listSet(setGoals)(i, "text", e.target.value)} data-testid={`goal-text-${i}`} placeholder="e.g. Reach 90% occupancy at 55 Broadway" className={inputCls} />
                <AttachmentEditor reportId={id} items={g.attachments || []} onChange={(v) => listSet(setGoals)(i, "attachments", v)} testid={`goal-${i}`} dndKey={`goals:${i}`} onMove={moveAttachment} />
              </>
            )} />

          {/* Sections */}
          <ListEditor title="Additional Sections" testid="sections" items={sections}
            onAdd={() => listAdd(setSections)({ heading: "", body: "" })}
            onDel={listDel(setSections)}
            render={(s, i) => (
              <>
                <input value={s.heading} onChange={(e) => listSet(setSections)(i, "heading", e.target.value)} data-testid={`section-heading-${i}`} placeholder="Section heading" className={`${inputCls} mb-2 font-serif`} />
                <textarea value={s.body} onChange={(e) => listSet(setSections)(i, "body", e.target.value)} data-testid={`section-body-${i}`} rows={3} placeholder="Body text" className={`${inputCls} resize-none`} />
                <AttachmentEditor reportId={id} items={s.attachments || []} onChange={(v) => listSet(setSections)(i, "attachments", v)} testid={`section-${i}`} dndKey={`sections:${i}`} onMove={moveAttachment} />
              </>
            )} />

          {/* Images */}
          <section className="space-y-3">
            <div className="flex items-center justify-between border-b border-brand-border pb-2">
              <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Photos</p>
              <label data-testid="report-image-add" className="inline-flex items-center gap-1.5 text-sm text-brand-primary hover:text-brand-primaryHover cursor-pointer">
                <ImagePlus className="h-4 w-4" /> Add photo
                <input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
              </label>
            </div>
            {images.length === 0 && <p className="text-[11px] text-brand-tertiary">Add photos of spaces, events or projects.</p>}
            <div className="grid grid-cols-2 gap-3">
              {images.map((im, i) => (
                <div key={im.id} className="border border-brand-border p-2" data-testid={`report-image-${i}`}>
                  <img src={im.url?.startsWith("http") ? im.url : `${process.env.REACT_APP_BACKEND_URL}${im.url}`} alt={im.caption || ""} className="w-full h-28 object-cover" crossOrigin="anonymous" />
                  <div className="flex items-center gap-1 mt-2">
                    <input value={im.caption || ""} onChange={(e) => listSet(setImages)(i, "caption", e.target.value)} data-testid={`report-image-caption-${i}`} placeholder="Caption" className="flex-1 bg-brand-page border border-brand-border rounded px-2 py-1.5 text-xs text-brand-ink focus:outline-none focus:border-brand-primary" />
                    <button onClick={() => listDel(setImages)(i)} data-testid={`report-image-remove-${i}`} className="p-1.5 text-brand-tertiary hover:text-brand-neg"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* PREVIEW */}
        <div className="lg:sticky lg:top-32 lg:self-start">
          <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary mb-3">Live Preview</p>
          <div className="border border-brand-border shadow-sm max-h-[calc(100vh-180px)] overflow-y-auto">
            <ReportView ref={previewRef} report={draft} />
          </div>
        </div>
      </main>
    </div>
  );
}

function ListEditor({ title, testid, items, onAdd, onDel, render }) {
  return (
    <section className="space-y-3" data-testid={`report-${testid}`}>
      <div className="flex items-center justify-between border-b border-brand-border pb-2">
        <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">{title}</p>
        <button onClick={onAdd} data-testid={`${testid}-add`} className="inline-flex items-center gap-1.5 text-sm text-brand-primary hover:text-brand-primaryHover"><Plus className="h-4 w-4" /> Add</button>
      </div>
      {items.length === 0 && <p className="text-[11px] text-brand-tertiary">None yet.</p>}
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-start bg-brand-surface border border-brand-border p-3">
            <GripVertical className="h-4 w-4 text-brand-tertiary mt-2 shrink-0" />
            <div className="flex-1 min-w-0">{render(it, i)}</div>
            <button onClick={() => onDel(i)} data-testid={`${testid}-del-${i}`} className="p-1.5 text-brand-tertiary hover:text-brand-neg shrink-0"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </section>
  );
}
